import type { CodeableConcept, ReferencedResourceId } from "./common";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import type { RegistrationReview } from "./patient-matching";
import type { PractitionerId } from "./practitioner.model";
import type { VisitId } from "./visit.model";

/** Stable manual Task identity, independent of the FHIR Task locator. */
export type TaskId = DomainId<"TaskId">;

export function asTaskId(value: string): TaskId {
  if (!value) throw new PreconditionFailedError("A task identity cannot be empty");
  return value as TaskId;
}

// ============================================================
// Task — a request that a person do something
//
// THE RESIDUE, and only the residue. Work is derived: the producer sweeps the
// chart and mints and retires rows on its own (ADR-0013). Almost nothing needs
// a record here, because doing the work leaves a trace — a Procedure appears, a
// QuestionnaireResponse completes — and the trace IS the fact a rule desires.
// This aggregate exists for work that leaves nothing behind: a phone call, a
// conversation, a judgement. Someone writes down that it was asked for, and
// later that it was done.
//
// IF THE WORK LEAVES A TRACE, IT DOES NOT BELONG HERE. Write a rule keyed on
// the trace instead, and it closes itself. A handler that creates one of these
// as a side effect of doing something else has almost certainly modelled
// derived work by hand.
//
// WHY STATUS LIVES HERE AND NOWHERE ELSE. The evaluator settles an item by
// reading evidence off the record it was minted from. With no record carrying
// "done", nothing derives completion and the producer re-mints the row forever.
// So this owns exactly three states — asked, done, retracted — and NOT who is
// doing it, whether they have started, or whether it is snoozed. That is roster
// state and belongs to the queue, which is the only writer of it.
//
// NOT the work-item Task. Those carry `@repo/work-queue`'s marker and are that
// package's private storage; the marker is the property line and this side
// never reads or writes across it.
// ============================================================

/**
 * Asked, and then one of two endings. `retracted` is not `done`: the work was
 * never carried out, and collapsing the two loses whether anything happened.
 */
export type TaskStatus = "open" | "done" | "retracted";

export const TASK_PRIORITIES = ["routine", "urgent", "asap", "stat"] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** Somebody named in a comment, so they hear about it. */
export interface TaskMention {
  resourceType: string;
  id: ReferencedResourceId;
  display?: string;
}

/** Who a request can be addressed to. A person, a team, or the practice itself. */
export const TASK_OWNER_TYPES = ["Practitioner", "Organization", "CareTeam"] as const;

export interface TaskOwner {
  resourceType: (typeof TASK_OWNER_TYPES)[number];
  id: PractitionerId | ReferencedResourceId;
}

export interface TaskComment {
  /** ISO instant the comment was made. */
  at: string;
  text: string;
  /** Practitioner id — absent for a comment with no attributable author. */
  authorId?: PractitionerId;
  /** Who was @-mentioned. The notification side reads these; nothing here does. */
  recipients?: TaskMention[];
}

/**
 * Where an OPEN request sits on somebody's list. Carried, never reasoned about:
 * this is roster state, and the queue is its only writer (see the header). It
 * lives on the props for exactly one reason — a create has no prior row for the
 * mapper to preserve it from, and "drafted, not yet released" is part of the
 * ask. No method below reads it, and no method below changes it.
 */
export const TASK_OPEN_STATUSES = ["draft", "ready", "in-progress", "on-hold"] as const;
export type TaskOpenStatus = (typeof TASK_OPEN_STATUSES)[number];

export interface TaskProps {
  registrationReview?: RegistrationReview;
  /** Absent until persisted. */
  id: TaskId;
  status?: TaskStatus;
  /** What is being asked for, in the requester's own words. */
  what: string;
  patientId?: PatientId;
  /** Practitioner id of whoever asked. */
  requestedBy?: PractitionerId;
  /** ISO date the request is wanted by. */
  dueBy?: string;
  priority?: TaskPriority;
  /** The visit this was asked for during, when it was asked during one. */
  encounterId?: VisitId;
  /** Roster state, opaque here — see {@link TaskOpenStatus}. */
  openStatus?: TaskOpenStatus;
  /**
   * Who it was addressed to when it was written down, as a full reference
   * (`Practitioner/123`, `CareTeam/…`). Roster state on the same terms as
   * {@link TaskOpenStatus}: a seed for the first write and nothing after, since
   * reassignment is the queue's move. Nothing below reads it.
   */
  assignedTo?: TaskOwner;
  comments: TaskComment[];
  /** ISO instant it ended, whichever way it ended. */
  closedAt?: string;
  /** Why it was retracted. Never set on a completion — "done" needs no reason. */
  retractedReason?: CodeableConcept;
}

/** Constructor input — `comments` defaults to none, because a new request has none. */
type NewTaskProps = Omit<TaskProps, "comments" | "status"> & {
  comments?: TaskComment[];
  status?: TaskStatus;
};

export class Task {
  private state: TaskProps & { status: TaskStatus };

  private constructor(props: TaskProps) {
    assertAsked(props.what);

    this.state = structuredClone({
      ...props,
      status: props.status ?? "open",
      comments: props.comments,
    });
  }

  static create(input: NewTaskProps): Task {
    if ((input.status ?? "open") !== "open") {
      throw new PreconditionFailedError(`Cannot ask for work already '${input.status}'`);
    }
    return new Task({
      ...structuredClone(input),
      id: asTaskId(input.id),
      status: "open",
      comments: input.comments ?? [],
    });
  }

  static openRegistrationReview(review: Omit<RegistrationReview, "resolution"> & { id: TaskId }): Task {
    const { id, ...registrationReview } = review;
    return Task.create({ id, what: "Possible duplicate patient", registrationReview });
  }

  /** A source registering again while staff are still deciding waits on that same review. */
  static pendingRegistrationReview(reviews: readonly Task[]): Task | undefined {
    return reviews.find((task) => task.isOpen && !!task.state.registrationReview);
  }

  static restore(snapshot: TaskProps): Task {
    return new Task(snapshot);
  }

  // ── facts ──

  get id(): TaskId {
    return this.state.id;
  }

  get status(): TaskStatus {
    return this.state.status;
  }

  /**
   * Still wants doing. The producer's whole notion of desire: an open request
   * should have a row, a closed one should not, and that is why closing is the
   * only thing here the queue reacts to.
   */
  get isOpen(): boolean {
    return this.state.status === "open";
  }

  get patientId(): PatientId | undefined {
    return this.state.patientId;
  }

  snapshot(): TaskProps {
    return structuredClone(this.state);
  }

  get rejectedRegistrationCandidates(): RegistrationReview["candidates"] {
    const review = this.state.registrationReview;
    if (review?.resolution?.kind !== "different-person") return [];
    return structuredClone(
      review.candidates.filter((_, index) => !review.resolution?.uncheckedCandidateIndexes?.includes(index)),
    );
  }

  resolveRegistration(resolution: NonNullable<RegistrationReview["resolution"]>, now: Date): void {
    this.assertOpen("resolve");
    const review = this.state.registrationReview;
    if (!review) throw new PreconditionFailedError("Task is not a registration review");
    if (resolution.kind === "link" && !review.candidates.some((c) => c.patientId === resolution.patientId)) {
      throw new PreconditionFailedError("Select one of the reviewed candidates");
    }
    if (
      resolution.uncheckedCandidateIndexes?.some(
        (index) => !Number.isInteger(index) || index < 0 || index >= review.candidates.length,
      )
    ) {
      throw new PreconditionFailedError("Unchecked candidate is not part of this review");
    }
    review.resolution = structuredClone(resolution);
    // Review contents belong to staff, never to the resolved patient compartment.
    this.state.patientId = undefined;
    this.complete(now);
  }

  // ── the two endings ──

  /** The thing that was asked for has been done. Idempotent. */
  complete(now: Date): void {
    if (this.state.status === "done") return;
    this.assertOpen("complete");
    if (this.state.registrationReview && !this.state.registrationReview.resolution) {
      throw new PreconditionFailedError("Resolve the possible duplicate before completing this task");
    }
    this.state.status = "done";
    this.state.closedAt = now.toISOString();
  }

  /**
   * Withdrawn — it was asked for and then called off, and nobody did it.
   * Never deleted: dropping the row loses that it was ever asked for.
   * Idempotent.
   */
  retract(now: Date, reason?: CodeableConcept): void {
    if (this.state.status === "retracted") return;
    this.assertOpen("retract");
    if (this.state.registrationReview && !this.state.registrationReview.resolution) {
      throw new PreconditionFailedError("Resolve the possible duplicate before retracting this task");
    }
    this.state.status = "retracted";
    this.state.closedAt = now.toISOString();
    if (reason) this.state.retractedReason = structuredClone(reason);
  }

  // ── the board ──
  //
  // Who is holding the request and where it sits on their list. No clinical
  // meaning, and deliberately separate from the two endings above: moving a
  // card is not an outcome, so nothing here records who did it or why.
  //
  // These are ON the aggregate rather than written past it. There is no illegal
  // owner and no illegal column, so the guard below is the only rule — but a
  // direct FHIR update has to send the whole Task, and two people working the
  // same board would then silently revert each other.

  /** Hand it to somebody. `ref` is a full reference — `Practitioner/123`, `CareTeam/…`. */
  assign(owner: TaskOwner): void {
    this.assertOpen("assign");
    if (!owner.id) throw new PreconditionFailedError("Task owner identity cannot be empty");
    this.state.assignedTo = structuredClone(owner);
  }

  /** Back onto the unassigned pile. Idempotent. */
  unassign(): void {
    this.assertOpen("unassign");
    this.state.assignedTo = undefined;
  }

  /**
   * Move it along the board. Terminal states are unspellable by type — an
   * ending is `complete` or `retract`, which record that it ended.
   */
  advance(status: TaskOpenStatus): void {
    this.assertOpen("advance");
    this.state.openStatus = status;
  }

  // ── the request itself ──

  /**
   * Revise the request. Only the named fields change — `dueBy: null` clears the
   * date, which is a different intent from leaving it alone, and the reason the
   * argument distinguishes absent from null.
   */
  revise(command: { what?: string; dueBy?: string | null; priority?: TaskPriority }): void {
    this.assertOpen("revise");
    if (command.what !== undefined) {
      assertAsked(command.what);
      this.state.what = command.what;
    }
    if (command.dueBy !== undefined) {
      this.state.dueBy = command.dueBy ?? undefined;
    }
    if (command.priority !== undefined) {
      this.state.priority = command.priority;
    }
  }

  /** Add to the conversation about this request. */
  comment(command: { text: string; authorId?: PractitionerId; recipients?: TaskMention[] }, now: Date): void {
    this.assertOpen("comment on");
    if (!command.text.trim()) {
      throw new PreconditionFailedError("A comment must say something");
    }
    this.state.comments.push({
      at: now.toISOString(),
      text: command.text,
      ...(command.authorId ? { authorId: command.authorId } : {}),
      ...(command.recipients?.length ? { recipients: structuredClone(command.recipients) } : {}),
    });
  }

  // ── internals ──

  private assertOpen(action: string): void {
    if (this.state.status !== "open") {
      throw new PreconditionFailedError(`Cannot ${action} a request that is already '${this.state.status}'`);
    }
  }
}

function assertAsked(what: string): void {
  if (!what.trim()) {
    throw new PreconditionFailedError("A task must say what is being asked for");
  }
}
